/**
 * Language switcher dropdown — rendered in both navbar and footer of the
 * public chrome. On select, persists the choice in cookie + localStorage,
 * then hard-navigates to the localized URL so SSR re-renders with the
 * correct `<html lang>` and prerendered locale bundle.
 *
 * Accessibility:
 *   - Trigger is a button (`aria-haspopup="listbox"`, `aria-expanded`).
 *   - Options use `role="option"` inside a `role="listbox"` container.
 *   - Keyboard: ArrowUp/Down/Home/End navigate, Enter selects, Escape closes.
 *   - Touch target ≥ 44×44px via `min-h-11` on the trigger button.
 *   - RTL-safe: logical CSS properties only; chevron mirrors in RTL.
 */

import { useCallback, useEffect, useId, useRef, useState } from "react";

import { getLocalizedHref, stripLocalePrefix } from "#/components/layout/PublicChrome.js";
import type { TranslateFn } from "#/lib/i18n.js";
import {
  LOCALES,
  LOCALE_COOKIE,
  LOCALE_STORAGE_KEY,
  type LocaleCode,
  getDescriptor,
} from "#/lib/i18n/locales.js";

const COOKIE_MAX_AGE_SECONDS = 31536000; // 1 year

type Variant = "navbar" | "footer";

export interface LanguageSwitcherProps {
  /** Active locale (canonical code, e.g. "zh-CN"). */
  currentLocale: LocaleCode;
  /**
   * Optional explicit logical path (e.g. "/outbreak/foo"). When omitted,
   * the switcher reads `window.location.pathname` at click time and strips
   * the locale prefix. SSR-safe because the live read only happens in the
   * onClick handler, never during render.
   */
  currentLogicalPath?: string;
  /** Visual variant — both render the same dropdown, only chrome differs. */
  variant: Variant;
  /** Translator from `createT(locale, "landing")`. */
  t: TranslateFn;
}

function persistChoice(locale: LocaleCode): void {
  if (typeof document !== "undefined") {
    document.cookie = `${LOCALE_COOKIE}=${encodeURIComponent(locale)}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;
  }
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    }
  } catch {
    // localStorage is unavailable in private mode / file:// — cookie alone is enough.
  }
}

export function LanguageSwitcher({
  currentLocale,
  currentLogicalPath,
  variant,
  t,
}: LanguageSwitcherProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [focusIndex, setFocusIndex] = useState<number>(
    Math.max(
      0,
      LOCALES.findIndex((l) => l.code === currentLocale),
    ),
  );
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const listboxRef = useRef<HTMLUListElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const listboxId = useId();
  const optionIdPrefix = useId();

  const currentDescriptor = getDescriptor(currentLocale);
  const triggerLabel = t("chrome.languageSwitcher.label");
  const ariaCurrent = t("chrome.languageSwitcher.current", {
    label: currentDescriptor.nativeLabel,
  });

  const handleSelect = useCallback(
    (next: LocaleCode) => {
      persistChoice(next);
      if (typeof window !== "undefined") {
        const path = currentLogicalPath ?? stripLocalePrefix(window.location.pathname);
        const dest = getLocalizedHref(next, path);
        // Hard navigation so SSR re-renders with correct <html lang> and
        // delivers the prerendered locale bundle.
        window.location.assign(dest);
      }
    },
    [currentLogicalPath],
  );

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    function onDocClick(event: MouseEvent) {
      const target = event.target as Node | null;
      if (containerRef.current && target && !containerRef.current.contains(target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // Focus the active option when the listbox opens. We resolve the option by
  // attribute selector (`data-option-index`) rather than by id so we don't
  // depend on `CSS.escape` being available (older jsdom builds lack the
  // `CSS` global).
  useEffect(() => {
    if (!open || !listboxRef.current) return;
    const el = listboxRef.current.querySelector<HTMLElement>(`[data-option-index="${focusIndex}"]`);
    el?.focus();
  }, [open, focusIndex, optionIdPrefix]);

  const onTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setOpen(true);
    }
  };

  const onListboxKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setFocusIndex((i) => (i + 1) % LOCALES.length);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setFocusIndex((i) => (i - 1 + LOCALES.length) % LOCALES.length);
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      setFocusIndex(0);
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      setFocusIndex(LOCALES.length - 1);
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      const target = LOCALES[focusIndex];
      if (target) handleSelect(target.code);
    }
  };

  const triggerSizing = variant === "navbar" ? "px-2 sm:px-3" : "px-2";

  return (
    <div ref={containerRef} className="relative inline-block" data-language-switcher={variant}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-label={triggerLabel}
        title={ariaCurrent}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onTriggerKeyDown}
        className={[
          "inline-flex min-h-11 max-w-full items-center gap-2 rounded-md",
          triggerSizing,
          "text-sm font-medium text-[color:var(--color-fg-muted)]",
          "no-underline transition-colors duration-150",
          "hover:bg-[color:var(--color-bg-surface)] hover:text-[color:var(--color-fg-default)]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent-info)]",
        ].join(" ")}
      >
        <span aria-hidden="true">🌐</span>
        <span className="max-w-[9rem] truncate">{currentDescriptor.nativeLabel}</span>
        <svg
          aria-hidden="true"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          className={open ? "rotate-180" : undefined}
          style={{ transition: "transform 150ms ease" }}
        >
          <path d="M2 4l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </button>

      {open && (
        <ul
          ref={listboxRef}
          id={listboxId}
          role="listbox"
          aria-label={t("chrome.languageSwitcher.chooseLanguage")}
          tabIndex={-1}
          onKeyDown={onListboxKeyDown}
          className={[
            "absolute z-50 mt-1 min-w-[14rem] max-h-[20rem] overflow-y-auto",
            "rounded-md border border-[color:var(--color-border-subtle)] bg-[color:var(--color-bg-base)] py-1 shadow-md",
            variant === "footer" ? "bottom-full mb-1" : "top-full",
            "end-0",
          ].join(" ")}
        >
          {LOCALES.map((descriptor, index) => {
            const selected = descriptor.code === currentLocale;
            return (
              <li
                key={descriptor.code}
                id={`${optionIdPrefix}-${index}`}
                data-option-index={index}
                role="option"
                aria-selected={selected}
                tabIndex={-1}
                lang={descriptor.htmlLang}
                onClick={() => handleSelect(descriptor.code)}
                onMouseEnter={() => setFocusIndex(index)}
                onFocus={() => setFocusIndex(index)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleSelect(descriptor.code);
                  }
                }}
                className={[
                  "flex min-h-11 cursor-pointer items-center justify-between gap-3 px-3",
                  "text-sm",
                  selected
                    ? "bg-[color:var(--color-bg-surface)] text-[color:var(--color-fg-default)]"
                    : "text-[color:var(--color-fg-default)]",
                  "hover:bg-[color:var(--color-bg-surface)]",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent-info)]",
                ].join(" ")}
              >
                <span className="font-medium">{descriptor.nativeLabel}</span>
                <span className="text-xs text-[color:var(--color-fg-subtle)]">
                  {descriptor.englishLabel}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
